import { useEffect, useState } from "react";
import { CircularProgress, IconButton, InputBase, Stack, Tooltip } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { useSnackbar } from "notistack";
import { useUpdateBalance } from "../../hooks/useAccessManagement";

// Credit balance, editable straight from the table: −/+ step it by one, or click the
// number to type an exact amount. Same balance the header's coin counter shows and that
// exports spend from (routes/users.js#resolveExportInfluencers).
//
// Every click saves immediately. An earlier version batched clicks behind a 700ms debounce
// to avoid chatty requests, but that made it impossible to tell whether anything had been
// saved — and a refresh inside that window silently dropped the change. Steps go out as a
// relative delta applied with $inc server-side, so several quick clicks still land
// correctly regardless of the order their responses come back in.
export default function BalanceCell({ user }) {
  const { enqueueSnackbar } = useSnackbar();
  const updateBalance = useUpdateBalance();

  const [value, setValue] = useState(user.balance ?? 0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  // Re-sync when the row's own data changes (any refetch of the list).
  useEffect(() => {
    setValue(user.balance ?? 0);
  }, [user.balance]);

  const save = async (payload, optimistic) => {
    const previous = value;
    setValue(optimistic);
    try {
      const response = await updateBalance.mutateAsync({ id: user.id, ...payload });
      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        setValue(previous);
        return;
      }
      setValue(response.balance);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not update balance", { variant: "error" });
      setValue(previous);
    }
  };

  const step = (amount) => {
    const next = Math.max(0, value + amount);
    if (next === value) return; // already at 0 — nothing to take away
    save({ delta: next - value }, next);
  };

  const commitDraft = () => {
    setEditing(false);
    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) return;

    const balance = Math.max(0, Math.round(parsed));
    if (balance === value) return;
    save({ balance }, balance);
  };

  const NUMBER_SX = {
    width: 58,
    px: 0.75,
    borderRadius: "8px",
    fontFamily: "Quicksand",
    fontWeight: 700,
    fontSize: 13,
    color: "#1C1C2E",
    "& input": { p: 0, textAlign: "center" },
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.25} onClick={(e) => e.stopPropagation()}>
      <Tooltip title="Remove 1 credit">
        <span>
          <IconButton size="small" onClick={() => step(-1)} disabled={value === 0}>
            <RemoveIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </span>
      </Tooltip>

      {editing ? (
        <InputBase
          autoFocus
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitDraft();
            if (e.key === "Escape") setEditing(false);
          }}
          sx={{ ...NUMBER_SX, border: "1px solid rgba(255,82,0,0.4)" }}
        />
      ) : (
        <Tooltip title="Click to set an exact amount">
          <InputBase
            readOnly
            value={value}
            onClick={() => {
              setDraft(String(value));
              setEditing(true);
            }}
            sx={{
              ...NUMBER_SX,
              cursor: "pointer",
              "& input": { ...NUMBER_SX["& input"], cursor: "pointer" },
              "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
            }}
          />
        </Tooltip>
      )}

      <Tooltip title="Add 1 credit">
        <span>
          <IconButton size="small" onClick={() => step(1)}>
            <AddIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Occupies a fixed slot so the row doesn't jiggle as it appears/disappears. */}
      <Stack sx={{ width: 16, alignItems: "center" }}>
        {updateBalance.isLoading && <CircularProgress size={12} sx={{ color: "#FF5200" }} />}
      </Stack>
    </Stack>
  );
}
